import { useId } from "react";

import type { HistoryMarker } from "../../../api/types";
import { Button, EmptyState, Pill } from "../../../ui";
import { markerIndex, MARKER_WORDS, stepIndexFor, type Step } from "../model/replay";
import { formatMoment } from "../model/time";

interface ReplayBarProps {
  steps: readonly Step[];
  markers: readonly HistoryMarker[];
  /** The change number the replay is at, or null for live. */
  cutoff: number | null;
  onChange(cutoff: number | null): void;
}

/**
 * The replay slider (DEC-1016-35). It steps through the recorded changes in the browser: the position is the
 * last change at or before it. Markers show where hand-overs were sent, SME reviews were made and restores were
 * done. Live brings the screen back to now; moving the slider to the end does the same.
 */
export function ReplayBar({ steps, markers, cutoff, onChange }: ReplayBarProps) {
  const id = useId();
  if (steps.length === 0) {
    return (
      <section className="card nv-replay" aria-labelledby={`${id}-title`}>
        <h2 id={`${id}-title`}>Replay</h2>
        <EmptyState title="Nothing has been recorded yet">The replay starts with the first change. Then you can move back and see how the ontology grew.</EmptyState>
      </section>
    );
  }
  const last = steps.length - 1;
  const index = stepIndexFor(steps, cutoff);
  const live = cutoff === null;
  const step = steps[index] as Step;
  const go = (to: number) => {
    const clamped = Math.max(0, Math.min(last, to));
    onChange(clamped >= last ? null : (steps[clamped] as Step).event);
  };
  const spot = (i: number) => (last === 0 ? 50 : (i / last) * 100);
  const words = `Change ${index + 1} of ${steps.length}, ${formatMoment(step.at)}`;
  return (
    <section className="card nv-replay" aria-labelledby={`${id}-title`}>
      <div className="row nv-replay__head">
        <h2 id={`${id}-title`}>Replay</h2>
        {live ? <Pill tone="note">Live</Pill> : <Pill>Looking at the past</Pill>}
        <Button variant="secondary" onClick={() => onChange(null)} disabled={live}>
          Back to live
        </Button>
      </div>
      <p className="muted" role="status">
        {live ? `Showing the discipline as it is now, after change ${steps.length}.` : `Showing the discipline as it stood on ${formatMoment(step.at)}, after change ${index + 1} of ${steps.length}.`}
      </p>
      <div className="nv-replay__controls">
        <Button variant="quiet" onClick={() => go(index - 1)} disabled={index === 0} aria-label="One change earlier">
          Earlier
        </Button>
        <div className="nv-replay__track">
          <input
            type="range"
            min={0}
            max={last}
            step={1}
            value={index}
            onChange={(event) => go(Number(event.target.value))}
            aria-label="Move through the recorded changes"
            aria-valuetext={words}
            disabled={steps.length < 2}
          />
          <div className="nv-replay__ticks" aria-hidden="true">
            {markers.map((marker) => (
              <span key={`${marker.kind}:${marker.event}:${marker.label}`} className="nv-replay__tick" data-kind={marker.kind} style={{ left: `${spot(markerIndex(steps, marker))}%` }} title={`${MARKER_WORDS[marker.kind]}: ${marker.label}`} />
            ))}
          </div>
        </div>
        <Button variant="quiet" onClick={() => go(index + 1)} disabled={live} aria-label="One change later">
          Later
        </Button>
      </div>
      {markers.length > 0 && (
        <div className="nv-replay__markers">
          <h3 className="nv-replay__marker-title">Go to a hand-over, a review or a restore</h3>
          <ul className="row">
            {markers.map((marker) => (
              <li key={`${marker.kind}:${marker.event}:${marker.label}`}>
                <button type="button" className="nv-marker" data-kind={marker.kind} onClick={() => go(markerIndex(steps, marker))}>
                  <span className="nv-marker__kind">{MARKER_WORDS[marker.kind]}</span> {marker.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="muted nv-replay__note">The replay shows each recorded change with who made it and when, restores included. It does not show the discussion.</p>
    </section>
  );
}
